'use client';

import { FC, useMemo, useState } from 'react';

import { UnitSystem } from '@ravago/shared/page-data/models/config';
import { NormalizedProduct, NormalizedTechnicalProperty } from '@ravago/shared/page-data/models/contentful-mapped';
import { ProductProperties } from '@ravago/shared/page-data/models/elements/extended-technical-properties-config.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { firstBy } from 'thenby';

import { PhysicalPropertiesUtils } from '../../../../utils/product/physical-properties.utils';
import { ItemInformationTable, Properties } from '../../../shared/item-information-table/item-information-table';
import { notchedImpactConfig, propertyUnitLabels } from '../../../shared/physical-property-display/mappers/property-unit-labels.mapper';
import { unitSystems } from '../../../shared/physical-property-display/mappers/unit-systems.mapper';
import { PhysicalProperty } from '../../../shared/physical-property-display/physical-property';
import { TitleStyling } from '../models/title-styling.model';

import { ClickableProductProperty } from './clickable-product-property';

const defaultValue = '-';

interface Props {
   locale: string;
   isConsumer: boolean;
   hasSustainabilityLabel: boolean;
   product: NormalizedProduct;
   titleStyling?: TitleStyling;
   brandBaseRoute?: string;
   producerBaseRoute?: string;
   technologyBaseRoute?: string;
   selectedProperties: ProductProperties[];
}

export const ExtendedTechnicalProperties: FC<Props> = ({
   locale,
   isConsumer,
   hasSustainabilityLabel,
   titleStyling,
   product,
   brandBaseRoute,
   producerBaseRoute,
   technologyBaseRoute,
   selectedProperties
}) => {
   const translate = useTranslations();
   const { phone } = useBreakpoint();

   const units = unitSystems(translate);
   const [unitSystem, setUnitSystem] = useState<UnitSystem>(units[0].id);
   const selectedUnit = useMemo(() => units.find((unit) => unit.id === unitSystem), [units, unitSystem]);

   const brandSlug = product.brand.slug;
   const brandNoFollow = product.brand.seoMetadata?.noFollow;
   const producerSlug = product.brand.producer?.slug;
   const producerNoFollow = product.brand.producer?.seoMetadata?.noFollow;
   const storefrontRoute = product.brand.producer?.storefront?.route;
   const storefrontNoFollow = product.brand.producer?.storefront?.seoMetadata?.noFollow;

   const { density, flowRate, technologies } = product;

   const flameRating = useMemo(() => {
      const flameRate = product.flameRating;
      return flameRate.filter((value) => value.unit === unitSystem);
   }, [product.flameRating, unitSystem]);

   const notchedImpact = useMemo(() => {
      const impact = product.notchedImpact.filter((value) => value.testingMethod?.includes('ASTM'));
      return impact.filter((value) => value.unit === unitSystem);
   }, [product.notchedImpact, unitSystem]);

   const notchedImpactStrength = useMemo(() => {
      const impactStrength = product.notchedImpactStrength.length
         ? product.notchedImpactStrength
         : product.notchedImpact.filter((value) => value.testingMethod?.includes('ISO'));
      return impactStrength.filter((value) => value.unit === unitSystem);
   }, [product.notchedImpact, product.notchedImpactStrength, unitSystem]);

   const densityProperty = useMemo(() => {
      const filteredDensity = density.filter((value) => value.unit === unitSystem);
      return filteredDensity?.length ? (
         <Box direction="column" gap="2xs">
            {filteredDensity.map((value) => (
               <PhysicalProperty key={`density-${value.value}-${value.testingMethod}`} value={value.value} badge={value.testingMethod} />
            ))}
         </Box>
      ) : undefined;
   }, [density, unitSystem]);

   const flowRateProperty = useMemo(() => {
      const filteredFlowRate = flowRate.filter((value) => value.unit === unitSystem);
      return filteredFlowRate?.length ? (
         <Box direction="column" gap="2xs">
            {filteredFlowRate
               .sort(
                  firstBy((v1: NormalizedTechnicalProperty, v2: NormalizedTechnicalProperty) =>
                     (Number.parseFloat(v1.value) ?? 0) > (Number.parseFloat(v2.value) ?? 0) ? 1 : -1
                  )
               )
               .map((value) => (
                  <PhysicalProperty
                     key={`flow-rate-${value.testingMethod}-${value.value}-${value.condition}`}
                     value={value.value}
                     badge={value.testingMethod}
                     condition={value.condition}
                  />
               ))}
         </Box>
      ) : undefined;
   }, [flowRate, unitSystem]);

   const flameRatingProperty = useMemo(
      () =>
         flameRating && flameRating?.length ? (
            <Box direction="column" gap="2xs">
               {flameRating
                  .sort(
                     firstBy('value', 'desc').thenBy((v1: NormalizedTechnicalProperty, v2: NormalizedTechnicalProperty) =>
                        PhysicalPropertiesUtils.getNumberFromCondition(unitSystem, v1.condition) >
                        PhysicalPropertiesUtils.getNumberFromCondition(unitSystem, v2.condition)
                           ? 1
                           : -1
                     )
                  )
                  .map((value, index) => (
                     <PhysicalProperty
                        key={`flame-rating-${value.value}-${value.condition}`}
                        badge={index === 0 ? value.testingMethod : undefined}
                        value={value.value}
                        condition={value.condition}
                     />
                  ))}
            </Box>
         ) : undefined,
      [flameRating]
   );

   const brandLink = useMemo(
      () =>
         brandSlug && brandBaseRoute ? (
            <ClickableProductProperty
               route={brandBaseRoute}
               id={`dynamic-link__extended-technical-properties-table__brand__${brandBaseRoute}`}
               slug={brandSlug}
               locale={locale}
               label={product.brand.name}
               isConsumer={isConsumer}
               noFollow={brandNoFollow}
            />
         ) : undefined,
      [brandBaseRoute, brandNoFollow, brandSlug, product.brand.name]
   );

   const technologiesLink = useMemo(
      () =>
         technologies && technologies.length && technologyBaseRoute
            ? technologies.map((technology, index) => (
                 <ClickableProductProperty
                    key={`extended-technical-properties-table__technology__${technology.slug}`}
                    index={index}
                    route={technologyBaseRoute}
                    id={`dynamic-link__extended-technical-properties-table__technology__${technology.slug}`}
                    slug={technology.slug}
                    locale={locale}
                    label={technology.name}
                    isConsumer={isConsumer}
                    noFollow={technology.seoMetadata?.noFollow}
                 />
              ))
            : undefined,
      [technologies, technologyBaseRoute]
   );

   const producerLink = useMemo(
      () =>
         (producerSlug && producerBaseRoute) || storefrontRoute ? (
            <ClickableProductProperty
               route={storefrontRoute || producerBaseRoute}
               id={storefrontRoute ? 'dynamic-link__product__storefront' : 'dynamic-link__product__producer'}
               slug={storefrontRoute ? undefined : producerSlug}
               locale={locale}
               label={product.brand.producer?.name ?? null}
               isConsumer={isConsumer}
               noFollow={storefrontRoute ? storefrontNoFollow : producerNoFollow}
            />
         ) : undefined,
      [producerBaseRoute, producerNoFollow, producerSlug, product.brand.producer?.name, storefrontNoFollow, storefrontRoute]
   );

   const notchedImpactProperty = (testingMethod: 'ASTM' | 'ISO') => {
      const chosenNotchedImpact = testingMethod === 'ASTM' ? notchedImpact : notchedImpactStrength;
      if (chosenNotchedImpact && chosenNotchedImpact?.length) {
         return (
            <Box direction="column" gap="2xs">
               {chosenNotchedImpact
                  .sort(
                     firstBy((v1: NormalizedTechnicalProperty, v2: NormalizedTechnicalProperty) =>
                        (Number.parseFloat(v1.value) ?? 0) > (Number.parseFloat(v2.value) ?? 0) ? 1 : -1
                     )
                  )
                  .map((value, index) => (
                     <PhysicalProperty
                        key={`notched-${testingMethod}-${value.value}-${value.condition}`}
                        value={value.value}
                        badge={index === 0 ? value.testingMethod : undefined}
                        condition={value.condition}
                     />
                  ))}
            </Box>
         );
      }
      return undefined;
   };

   const switchUnitSystem = (value?: DropdownOption) => {
      if (value) setUnitSystem(value.id as UnitSystem);
   };

   const properties: Properties = {
      ...(selectedProperties?.includes('brand') && {
         brand: {
            label: translate('product.information.brand'),
            value: brandLink || product.brand.name || defaultValue,
            dataCy: '360brand'
         }
      }),
      ...(selectedProperties?.includes('producer') && {
         producer: {
            label: translate('product.information.producer'),
            value: producerLink || product.brand.producer?.name || defaultValue,
            dataCy: '360producer'
         }
      }),
      ...(selectedProperties?.includes('polymer-type') && {
         polymer: {
            label: translate('product.information.polymer-type'),
            value: product.polymerSubGroup || defaultValue
         }
      }),
      ...(selectedProperties?.includes('filler') && {
         fillers: {
            label: translate('product.information.filler'),
            value:
               product.fillers.map((filler) => `${filler.percentage}% ${filler.filler?.name}`).join(', ') || translate('product.information.unfilled')
         }
      }),
      ...(selectedProperties?.includes('form') && {
         form: {
            label: translate('product.information.form'),
            value: product.form || defaultValue
         }
      }),
      ...(selectedProperties?.includes('technology') && {
         technology: {
            label: translate('products-overview-filters-technology'),
            value: technologiesLink || technologies.map((tech) => tech.name).join(' • ') || defaultValue,
            dataCy: '360technology'
         }
      }),
      ...(selectedProperties?.includes('features') && {
         features: {
            label: translate('products-features'),
            value: product.features.map((feature) => feature.name).join(' • ') || defaultValue
         }
      }),
      ...(!hasSustainabilityLabel &&
         selectedProperties?.includes('quality') && {
            quality: {
               label: translate('product.information.quality'),
               value: product.quality?.name || defaultValue
            }
         }),
      ...(hasSustainabilityLabel &&
         selectedProperties?.includes('quality') && {
            sustainability: {
               label: translate('product.information.sustainability'),
               value:
                  (product.sustainability?.sustainabilityGroup
                     ? `${product.sustainability?.sustainabilityGroup?.name} - ${product.sustainability?.name}`
                     : product.sustainability?.name) || defaultValue
            }
         }),
      density: {
         label: `${translate('product.information.physical-properties.density')} ${propertyUnitLabels.density[unitSystem]}`,
         value: densityProperty || defaultValue
      },
      flowRateProperty: {
         label: `${translate('product.information.physical-properties.flow-rate')} ${propertyUnitLabels.flowRate[unitSystem]}`,
         value: flowRateProperty || defaultValue
      },
      flameRatingProperty: {
         label: translate('product.information.physical-properties.flame-rating'),
         value: flameRatingProperty || defaultValue
      },
      notchedImpactPropertyAstm: {
         label: translate('products-overview-filters-notched-izod-impact-dynamic', notchedImpactConfig.ASTM[unitSystem]),
         value: notchedImpactProperty('ASTM') || defaultValue
      },
      notchedImpactPropertyIso: {
         label: translate('products-overview-filters-notched-izod-impact-dynamic', notchedImpactConfig.ISO[unitSystem]),
         value: notchedImpactProperty('ISO') || defaultValue
      }
   };

   return (
      <Box direction="column">
         <Box justify={phone ? undefined : 'between'} direction={phone ? 'column' : 'row'}>
            {titleStyling?.input && (
               <Typography color={titleStyling.colour} variant={titleStyling?.variant} component={titleStyling?.component}>
                  {titleStyling?.input}
               </Typography>
            )}
            <Box>
               <Dropdown
                  id="extended-technical-properties-table__dropdown"
                  emptyOption={false}
                  label={translate('product.information.physical-properties.unit-of-measure')}
                  options={units}
                  value={selectedUnit}
                  onChange={switchUnitSystem}
               />
            </Box>
         </Box>
         <ItemInformationTable rounded properties={properties} rowDividers greyHeader />
      </Box>
   );
};
